import { useState } from "react";
import { MessageCircleWarning } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SendFeedbackModal } from "./SendFeedbackModal";
import { cn } from "@/lib/utils";

const FeedbackIcon = ({ className }: { className?: string }) => {
  return (
    <>
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-linear-to-br from-blue-600 to-purple-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      />
      <MessageCircleWarning
        className={cn(
          "absolute h-4 w-4 sm:h-5 sm:w-5 transition-opacity duration-300 group-hover:opacity-0",
          className,
        )}
        stroke="url(#feedback-gradient)"
      />
      <MessageCircleWarning
        className={cn(
          "relative h-4 w-4 sm:h-5 sm:w-5 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100",
          className,
        )}
      />
    </>
  );
};

type Variant = "header-button" | "menu-item" | "nav-item" | "footer-link";

interface SendFeedbackTriggerProps {
  variant: Variant;
  onSelect?: () => void;
  className?: string;
}

export const SendFeedbackTrigger = ({
  variant,
  onSelect,
  className,
}: SendFeedbackTriggerProps) => {
  const [open, setOpen] = useState(false);

  const openModal = () => {
    onSelect?.();
    setOpen(true);
  };

  return (
    <>
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs>
          <linearGradient
            id="feedback-gradient"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2="24"
            y2="24"
          >
            <stop offset="0%" stopColor="#2563eb" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
        </defs>
      </svg>

      {variant === "header-button" && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={openModal}
          aria-label="Send feedback"
          className={cn(
            "group relative shrink-0 min-w-touch min-h-touch h-8 w-8 sm:h-9 sm:w-9 p-0 overflow-hidden hover:bg-transparent",
            className,
          )}
        >
          <FeedbackIcon className="w-4 h-4" />
        </Button>
      )}

      {variant === "menu-item" && (
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            openModal();
          }}
          className={cn(
            "flex items-center gap-2 cursor-pointer min-h-touch",
            className,
          )}
        >
          <FeedbackIcon className="w-4 h-4" />
          <span className="bg-linear-to-br from-blue-600 to-purple-600 text-transparent bg-clip-text">
            Send feedback
          </span>
        </DropdownMenuItem>
      )}

      {variant === "nav-item" && (
        <button
          type="button"
          onClick={openModal}
          className="flex items-center gap-2 py-3 px-4 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors min-h-touch w-full text-left"
        >
          <FeedbackIcon className="w-4 h-4" />
          Send feedback
        </button>
      )}

      {variant === "footer-link" && (
        <button
          type="button"
          onClick={openModal}
          className="w-fit hover:underline text-left"
        >
          Send feedback
        </button>
      )}

      <SendFeedbackModal open={open} onOpenChange={setOpen} />
    </>
  );
};
